//@ts-ignore
import Prism from 'prismjs'
import { useContext, useEffect, useRef, useState } from 'react'
import { IconX, IconDownload, IconCopy } from '@tabler/icons-react'
import { ChatbotContext } from '../context/ChatbotProvider'
import { useTrackEvent } from '@microsoft/applicationinsights-react-js'
import { appInsights } from '../../../../_services/AppInsights'
import { EventType } from '../../../../utility/types'
import { getFileExtension } from '../utils/constants'
import { useThemeManager } from '../../../../_hooks/useThemeManager'

interface CodeWindowProps {
  isHistoryOpened?: boolean
}

export const CodeWindow: React.FC<CodeWindowProps> = ({ isHistoryOpened }) => {
  const { selectedMessage, setSelectedMessage } = useContext(ChatbotContext)
  const lang = selectedMessage?.lang || 'text'
  const [opening, setOpening] = useState(false)
  const [closing, setClosing] = useState(false)
  const [isCopied, setIsCopied] = useState(false)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  //@ts-ignore
  const trackCodeMaximize = useTrackEvent(appInsights, EventType.IsMaximizeCodeClicked)
  const { isDarkTheme } = useThemeManager()
  const theme = isDarkTheme ? 'dark' : 'primary'
  useEffect(() => {
    trackCodeMaximize({})
  }, [selectedMessage])

  const highlightedCode = Prism.highlight(
    selectedMessage?.code || '',
    Prism.languages[lang] || Prism.languages.markup,
    lang
  )

  // Split highlighted code into lines for line numbering
  const codeLines = highlightedCode.split('\n')

  useEffect(() => {
    const openTimer = setTimeout(() => {
      setOpening(selectedMessage)
    }, 300)

    return () => {
      clearTimeout(openTimer)
    }
  }, [selectedMessage])

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current)
      }
      if (copyTimerRef.current) {
        clearTimeout(copyTimerRef.current)
      }
    }
  }, [])

  if (!selectedMessage) return null

  const handleClose = () => {
    setClosing(true)
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
    }
    closeTimerRef.current = setTimeout(() => {
      setSelectedMessage(null)
    }, 300) // Match this duration with your CSS transition duration
  }
  const onCopy = (text: string) => {
    setIsCopied(true)
    navigator.clipboard.writeText(text)
    if (copyTimerRef.current) {
      clearTimeout(copyTimerRef.current)
    }
    copyTimerRef.current = setTimeout(() => {
      setIsCopied(false)
    }, 3000)
  }

  return (
    <div
      data-testid="code-window-container"
      className={`flex h-full min-h-0 flex-col self-stretch flex-grow citations overflow-hidden w-[50%] theme-surface theme-border-default border transition-all duration-300 ease-in-out transform 
            ${isHistoryOpened ? 'max-w-[55%]' : 'max-w-[65%]'} 
            ${opening && !closing ? 'translate-x-0 opacity-100' : ''} 
            ${closing ? 'translate-x-full opacity-0 closing' : ''}`}>
      <div className="code-header h-[35px] flex justify-between items-center px-1">
        <span className="language">{lang}</span>
        <div className="flex items-center">
          {isCopied ? (
            <span className="me-50">Copied!</span>
          ) : (
            <IconCopy
              onClick={() => onCopy(selectedMessage?.code)}
              className="me-50 cursor-pointer"
              size={15}
              data-testid="copy-button"
            />
          )}
          <IconDownload
            onClick={() => {
              const extension = getFileExtension(lang)
              const blob = new Blob([selectedMessage?.code || ''], {
                type: 'text/plain;charset=utf-8'
              })
              const url = URL.createObjectURL(blob)
              const anchor = document.createElement('a')
              anchor.href = url
              anchor.download = `code-snippet.${extension}`
              document.body.appendChild(anchor)
              anchor.click()
              document.body.removeChild(anchor)
              URL.revokeObjectURL(url)
            }}
            className="me-50 cursor-pointer"
            size={15}
            data-testid="download-button"
          />
          <IconX
            size={18}
            onClick={handleClose}
            className="cursor-pointer"
            data-testid="close-button"
          />
        </div>
      </div>
      <div
        className={`d-flex code-block m-0 relative w-full flex-1 min-h-0 overflow-auto bg-[var(--surface-dark-bg)] ${theme}`}>
        <pre className={`m-0 min-h-full w-full line-numbers language-${lang}`}>
          <code>
            {codeLines.map((line: any, index: number) => (
              <div key={index} className="flex">
                <span className="line-number border-r border-[var(--theme-border)]">
                  {index + 1}
                </span>
                <span dangerouslySetInnerHTML={{ __html: line }} />
              </div>
            ))}
          </code>
        </pre>
      </div>
    </div>
  )
}
